import { Role, RoleEnum, SubRoleEnum } from "@dune/vars";
import { APP_PAGES } from "../pages";
import {  SubRole } from "../users/users.types";
import { TaskCTA, TaskCTAType } from "./tasks.types";


export const TaskTemplateKey = {
	PARTNERSHIP_REQUEST: 'PARTNERSHIP_REQUEST',
	ONBOARDING_ANALYSIS: 'ONBOARDING_ANALYSIS',
	GENERATE_CONVENTION: 'GENERATE_CONVENTION',
	SEND_CONVENTION: 'SEND_CONVENTION',
	SIGN_CONVENTION_APPORTEUR: 'SIGN_CONVENTION_APPORTEUR',
	SIGN_CONVENTION_DUNE: 'SIGN_CONVENTION_DUNE',
	MARK_CONVENTION_AS_SIGNED: 'MARK_CONVENTION_AS_SIGNED',
	RENEW_CONVENTION: 'RENEW_CONVENTION',
	VALIDATE_RENEW_CONVENTION: 'VALIDATE_RENEW_CONVENTION',
	GENERATE_AMENDMENT_CONVENTION: 'GENERATE_AMENDMENT_CONVENTION',
	SEND_AMENDMENT_CONVENTION: 'SEND_AMENDMENT_CONVENTION',
	SIGN_AMENDMENT_CONVENTION_APPORTEUR: 'SIGN_AMENDMENT_CONVENTION_APPORTEUR',
	SIGN_AMENDMENT_CONVENTION_DUNE: 'SIGN_AMENDMENT_CONVENTION_DUNE',

	// Construction site tasks
	CONSTRUCTION_SITE_PRICING_INDICATION: 'CONSTRUCTION_SITE_PRICING_INDICATION',
	ANALYZE_CONSTRUCTION_SITE_SUBMISSION: 'ANALYZE_CONSTRUCTION_SITE_SUBMISSION',
	SUBMIT_CONSTRUCTION_SITE: "SUBMIT_CONSTRUCTION_SITE",
	CONSTRUCTION_SITE_RECEPTION: "CONSTRUCTION_SITE_RECEPTION",
	ANALYZE_CONSTRUCTION_SITE_RECEPTION: "ANALYZE_CONSTRUCTION_SITE_RECEPTION",
	GENERATE_END_OF_WORK_AMENDMENT: "GENERATE_END_OF_WORK_AMENDMENT",

	// Proposition workflow tasks
	GENERATE_PROPOSITION: 'GENERATE_PROPOSITION',
	SEND_PROPOSITION: 'SEND_PROPOSITION',
	LIFT_PROPOSITION_RESERVES: 'LIFT_PROPOSITION_RESERVES',
	SIGN_PROPOSITION_BROKER: 'SIGN_PROPOSITION_BROKER',
	SIGN_PROPOSITION_DUNE: 'SIGN_PROPOSITION_DUNE',
	GENERATE_QUITTANCEMENT: 'GENERATE_QUITTANCEMENT',
	GENERATE_CONTRACT: 'GENERATE_CONTRACT',
	SEND_CONTRACT: 'SEND_CONTRACT',
	SIGN_CONTRACT_BROKER: 'SIGN_CONTRACT_BROKER',
	SIGN_CONTRACT_DUNE: 'SIGN_CONTRACT_DUNE',
	GENERATE_FINAL_CERTIFICATE: 'GENERATE_FINAL_CERTIFICATE',
	GENERATE_QUITTANCEMENT_RECEPTION: 'GENERATE_QUITTANCEMENT_RECEPTION',
	SEND_END_OF_WORK_AMENDMENT: 'SEND_END_OF_WORK_AMENDMENT',
	SIGN_END_OF_WORK_AMENDMENT_BROKER: 'SIGN_END_OF_WORK_AMENDMENT_BROKER',
	SIGN_END_OF_WORK_AMENDMENT_DUNE: 'SIGN_END_OF_WORK_AMENDMENT_DUNE',
	GENERATE_PROPOSITION_DOCUMENT: 'GENERATE_PROPOSITION_DOCUMENT',

	// GFA tasks
	SUBMIT_GFA: 'SUBMIT_GFA',
	ANALYZE_GFA_SUBMISSION: 'ANALYZE_GFA_SUBMISSION',
	GENERATE_GFA_DOCUMENT: 'GENERATE_GFA_DOCUMENT',

	// Automatic reminder tasks
	REMINDER_SIGN_PROPOSITION_1: 'REMINDER_SIGN_PROPOSITION_1',
	REMINDER_SIGN_PROPOSITION_2: 'REMINDER_SIGN_PROPOSITION_2',
	REMINDER_SIGN_CONTRACT_1: 'REMINDER_SIGN_CONTRACT_1',
	REMINDER_SIGN_CONTRACT_2: 'REMINDER_SIGN_CONTRACT_2',
	REMINDER_SIGN_END_OF_WORK_AMENDMENT_1: 'REMINDER_SIGN_END_OF_WORK_AMENDMENT_1',
	REMINDER_SIGN_END_OF_WORK_AMENDMENT_2: 'REMINDER_SIGN_END_OF_WORK_AMENDMENT_2',
	REMINDER_PAYMENT_PRIME_1: 'REMINDER_PAYMENT_PRIME_1',
	REMINDER_PAYMENT_PRIME_2: 'REMINDER_PAYMENT_PRIME_2',
	REMINDER_PAYMENT_PRIME_RECEPTION_1: 'REMINDER_PAYMENT_PRIME_RECEPTION_1',
	REMINDER_PAYMENT_PRIME_RECEPTION_2: 'REMINDER_PAYMENT_PRIME_RECEPTION_2',

	// Special actions
	POLICE_TERMINATION: 'POLICE_TERMINATION',
} as const;

export type TaskTemplateKeyValues = typeof TaskTemplateKey[keyof typeof TaskTemplateKey];

export const RECEPTION_TASKS = [
	TaskTemplateKey.GENERATE_FINAL_CERTIFICATE,
	TaskTemplateKey.GENERATE_QUITTANCEMENT_RECEPTION,
	TaskTemplateKey.REMINDER_PAYMENT_PRIME_RECEPTION_1,
	TaskTemplateKey.REMINDER_PAYMENT_PRIME_RECEPTION_2,
] as const;

export type ReceptionTaskTemplateKey = typeof RECEPTION_TASKS[number];

export interface StaticDueDate {
	month: number;
	day: number;
	yearOffset?: number;
}

export interface CatalogEntry {
  defaultTitle: string;
  defaultAssignmentRole?: Role;
  defaultAssignmentSubRole?: SubRole;
  visibilityRoles: Role[];
  dashboardVisibilityRoles?: Role[];
  dashboardVisibilitySubRoles?: SubRole[];
  visibilitySubroles?: SubRole[];
  next?: TaskTemplateKeyValues[];
  dueInDays?: number;
  staticDue?: StaticDueDate;
  cta?: TaskCTA;
}

export const TASK_CATALOG: Record<TaskTemplateKeyValues, CatalogEntry> = {
	[TaskTemplateKey.GENERATE_END_OF_WORK_AMENDMENT]: {
		defaultTitle: "Génération de l'avenant fin de travaux",
		defaultAssignmentRole: RoleEnum.DUNE,
		visibilityRoles: [RoleEnum.DUNE],
	},
	[TaskTemplateKey.PARTNERSHIP_REQUEST]: {
		defaultTitle: "Demande de partenariat",
		defaultAssignmentSubRole: SubRoleEnum.BROKER_ADMIN,
		visibilityRoles: [RoleEnum.DUNE],
		next: [TaskTemplateKey.ONBOARDING_ANALYSIS],
		dueInDays: 3,
		cta: {
			label: "Remplir le formulaire",
			actionType: TaskCTAType.REDIRECT,
			navigationPath: "/register-broker-second-step/:id",
		},
	},
  [TaskTemplateKey.ONBOARDING_ANALYSIS]: {
    defaultTitle: "Analyse d'onboarding",
    defaultAssignmentSubRole: SubRoleEnum.DUNE_ADMIN,
    visibilityRoles: [RoleEnum.DUNE],
	dashboardVisibilitySubRoles: [SubRoleEnum.DUNE_ADMIN],
    next: [TaskTemplateKey.GENERATE_CONVENTION],
    dueInDays: 7,
    cta: {
      label: "Analyser la demande",
      actionType: TaskCTAType.REDIRECT,
      navigationPath: APP_PAGES.PARTNERSHIP_REQUEST_ANALYSIS.index,
    },
  },
  [TaskTemplateKey.GENERATE_CONVENTION]: {
    defaultTitle: "Génération de la convention",
    defaultAssignmentSubRole: SubRoleEnum.DUNE_ADMIN,
    visibilityRoles: [RoleEnum.DUNE],
	dashboardVisibilitySubRoles: [SubRoleEnum.DUNE_ADMIN],
    dueInDays: 14,
    next: [TaskTemplateKey.SEND_CONVENTION],
    cta: {
      label: "Générer la convention",
      actionType: TaskCTAType.MODAL,
      modalKey: "generate-convention",
    },
  },
  [TaskTemplateKey.SEND_CONVENTION]: {
    defaultTitle: "Envoi de la convention",
    defaultAssignmentSubRole: SubRoleEnum.DUNE_ADMIN,
    visibilityRoles: [RoleEnum.DUNE],
	dashboardVisibilitySubRoles: [SubRoleEnum.DUNE_ADMIN],
    dueInDays: 1,
    next: [TaskTemplateKey.SIGN_CONVENTION_APPORTEUR, TaskTemplateKey.SIGN_CONVENTION_DUNE],
    cta: {
      label: "Envoyer la convention",
      actionType: TaskCTAType.ENDPOINT,
      endpoint: "/documents/send-convention",
    },
  },
  [TaskTemplateKey.SIGN_CONVENTION_APPORTEUR]: {
    defaultTitle: "Signature de la convention par l'apporteur",
    defaultAssignmentSubRole: SubRoleEnum.BROKER_GESTIONNAIRE,
    visibilityRoles: [RoleEnum.BROKER],
	visibilitySubroles: [SubRoleEnum.DUNE_ADMIN],
    dueInDays: 7,
  },
  [TaskTemplateKey.SIGN_CONVENTION_DUNE]: {
    defaultTitle: "Signature de la convention par DUNE",
    defaultAssignmentSubRole: SubRoleEnum.DUNE_ADMIN,
    visibilityRoles: [RoleEnum.DUNE],
	dashboardVisibilitySubRoles: [SubRoleEnum.DUNE_ADMIN],
    dueInDays: 1,
    cta: {
      label: "Signer la convention",
      actionType: TaskCTAType.MODAL,
      modalKey: "sign-convention-dune",
    },
  },
  [TaskTemplateKey.MARK_CONVENTION_AS_SIGNED]: {
    defaultTitle: "Marquer la convention comme signée",
    defaultAssignmentRole: RoleEnum.DUNE,
    visibilityRoles: [RoleEnum.DUNE],
    dueInDays: 7,
    cta: {
      label: "Marquer comme signée",
      actionType: TaskCTAType.MODAL,
      modalKey: "mark-convention-signed",
    },
  },
	[TaskTemplateKey.RENEW_CONVENTION]: {
		defaultTitle: "Renouvellement de la convention",
		defaultAssignmentRole: RoleEnum.BROKER,
		visibilityRoles: [RoleEnum.BROKER, RoleEnum.DUNE],
		staticDue: { month: 2, day: 28, yearOffset: 1 },
		next: [TaskTemplateKey.VALIDATE_RENEW_CONVENTION],
	},
	[TaskTemplateKey.VALIDATE_RENEW_CONVENTION]: {
		defaultTitle: "Validation du renouvellement de la convention",
		defaultAssignmentRole: RoleEnum.DUNE,
		visibilityRoles: [RoleEnum.DUNE],
		dueInDays: 3,
	},
	[TaskTemplateKey.GENERATE_AMENDMENT_CONVENTION]: {
		defaultTitle: "Génération de l'avenant à la convention",
		defaultAssignmentRole: RoleEnum.DUNE,
		visibilityRoles: [RoleEnum.DUNE],
		dueInDays: 1,
		next: [TaskTemplateKey.SEND_AMENDMENT_CONVENTION],
	},
	[TaskTemplateKey.SEND_AMENDMENT_CONVENTION]: {
		defaultTitle: "Envoi de l'avenant à la convention",
		defaultAssignmentRole: RoleEnum.DUNE,
		visibilityRoles: [RoleEnum.DUNE],
		dueInDays: 1,
		next: [TaskTemplateKey.SIGN_AMENDMENT_CONVENTION_APPORTEUR],
	},
	[TaskTemplateKey.SIGN_AMENDMENT_CONVENTION_APPORTEUR]: {
		defaultTitle: "Signature de l'avenant par l'apporteur",
		defaultAssignmentRole: RoleEnum.BROKER,
		visibilityRoles: [RoleEnum.BROKER, RoleEnum.DUNE],
		dueInDays: 7,
		next: [TaskTemplateKey.SIGN_AMENDMENT_CONVENTION_DUNE],
	},
	[TaskTemplateKey.SIGN_AMENDMENT_CONVENTION_DUNE]: {
		defaultTitle: "Signature de l'avenant par DUNE",
		defaultAssignmentRole: RoleEnum.DUNE,
		visibilityRoles: [RoleEnum.DUNE],
		dueInDays: 1,
	},

	// Construction site tasks
	[TaskTemplateKey.CONSTRUCTION_SITE_PRICING_INDICATION]: {
		defaultTitle: "Indication tarifaire Chantier",
		defaultAssignmentRole: RoleEnum.BROKER,
		visibilityRoles: [RoleEnum.BROKER, RoleEnum.DUNE],
		dueInDays: 30,
		cta: {
			label: "Compléter le formulaire",
			actionType: TaskCTAType.REDIRECT,
			navigationPath: APP_PAGES.PROJECTS.binders.show.index,
		},
	},
	[TaskTemplateKey.SUBMIT_CONSTRUCTION_SITE]: {
		defaultTitle: "Soumission Chantier",
		defaultAssignmentRole: RoleEnum.BROKER,
		visibilityRoles: [RoleEnum.BROKER, RoleEnum.DUNE],
		cta: {
			label: "Soumettre le chantier",
			actionType: TaskCTAType.REDIRECT,
			navigationPath: APP_PAGES.PROJECTS.submission.index,
		},
	},
	[TaskTemplateKey.ANALYZE_CONSTRUCTION_SITE_SUBMISSION]: {
		defaultTitle: "Analyse Chantier",
		defaultAssignmentRole: RoleEnum.DUNE,
		visibilityRoles: [RoleEnum.DUNE],
		// next: [TaskTemplateKey.GENERATE_PROPOSITION],
		cta: {
			label: "Analyser le chantier",
			actionType: TaskCTAType.REDIRECT,
			navigationPath: APP_PAGES.PROJECTS.analysis.index,
		},
	},
	[TaskTemplateKey.CONSTRUCTION_SITE_RECEPTION]: {
		defaultTitle: "Réception chantier",
		defaultAssignmentRole: RoleEnum.BROKER,
		visibilityRoles: [RoleEnum.DUNE, RoleEnum.BROKER],
		dashboardVisibilityRoles : [RoleEnum.BROKER],
		next: [TaskTemplateKey.ANALYZE_CONSTRUCTION_SITE_RECEPTION],
		cta: {
			label: "Réceptionner le chantier",
			actionType: TaskCTAType.REDIRECT,
			navigationPath: APP_PAGES.PROJECTS.reception.index,
		},
	},
	[TaskTemplateKey.ANALYZE_CONSTRUCTION_SITE_RECEPTION]: {
		defaultTitle: "Analyse réception chantier",
		defaultAssignmentRole: RoleEnum.DUNE,
		visibilityRoles: [RoleEnum.DUNE],
		dueInDays: 3,
		cta: {
			label: "Analyser la réception",
			actionType: TaskCTAType.REDIRECT,
			navigationPath: APP_PAGES.PROJECTS.receptionAnalysis.index,
		},
	},

	// Proposition workflow tasks
	[TaskTemplateKey.GENERATE_PROPOSITION]: {
		defaultTitle: "Générer la proposition",
		defaultAssignmentRole: RoleEnum.DUNE,
		visibilityRoles: [RoleEnum.DUNE],
		dashboardVisibilitySubRoles: [SubRoleEnum.DUNE_UNDERWRITER],
		next: [TaskTemplateKey.SEND_PROPOSITION],
		cta: {
			label: "Générer la proposition",
			actionType: TaskCTAType.MODAL,
			modalKey: "generate-proposition",
		},
	},
	[TaskTemplateKey.LIFT_PROPOSITION_RESERVES]: {
		defaultTitle: "Lever les réserves de la proposition",
		defaultAssignmentRole: RoleEnum.BROKER,
		visibilityRoles: [RoleEnum.DUNE],
		dashboardVisibilityRoles: [RoleEnum.BROKER],
		dueInDays: 30,
		cta: {
			label: "Visualiser les réserves",
			actionType: TaskCTAType.REDIRECT,
			navigationPath: APP_PAGES.PROPOSALS.show.index,
		},
	},
	[TaskTemplateKey.SEND_PROPOSITION]: {
		defaultTitle: "Envoyer la proposition en signature",
		visibilityRoles: [RoleEnum.DUNE],
		defaultAssignmentRole: RoleEnum.BROKER,
		dashboardVisibilityRoles: [RoleEnum.BROKER],
		next: [TaskTemplateKey.SIGN_PROPOSITION_BROKER],
		cta: {
			label: "Envoyer pour signature",
			actionType: TaskCTAType.MODAL,
			modalKey: "send-proposition",
		},
	},
	[TaskTemplateKey.SIGN_PROPOSITION_BROKER]: {
		defaultTitle: "Proposition à signer Apporteur",
		visibilityRoles: [RoleEnum.DUNE, RoleEnum.BROKER],
		dueInDays: 30,
		next: [TaskTemplateKey.SIGN_PROPOSITION_DUNE],
	},
	[TaskTemplateKey.SIGN_PROPOSITION_DUNE]: {
		defaultTitle: "Proposition à signer DUNE",
		defaultAssignmentRole: RoleEnum.DUNE,
		visibilityRoles: [RoleEnum.DUNE],
		dashboardVisibilitySubRoles: [SubRoleEnum.DUNE_UNDERWRITER],
		dueInDays: 1,
		next: [TaskTemplateKey.GENERATE_QUITTANCEMENT],
		cta: {
			label: "Signer la proposition",
			actionType: TaskCTAType.MODAL,
			modalKey: "sign-proposition-dune",
		},
	},
	[TaskTemplateKey.GENERATE_QUITTANCEMENT]: {
		defaultTitle: "Quittancement",
		defaultAssignmentRole: RoleEnum.DUNE,
		visibilityRoles: [RoleEnum.DUNE],
		dashboardVisibilitySubRoles: [SubRoleEnum.DUNE_UNDERWRITER],
		dueInDays: 1,
		cta: {
			label: "Générer la quittance",
			actionType: TaskCTAType.MODAL,
			modalKey: "generate-receipt",
		},
	},
	[TaskTemplateKey.GENERATE_CONTRACT]: {
		defaultTitle: "Générer le contrat",
		defaultAssignmentRole: RoleEnum.DUNE,
		visibilityRoles: [RoleEnum.DUNE],
		dashboardVisibilitySubRoles: [SubRoleEnum.DUNE_UNDERWRITER],
		dueInDays: 1,
		next: [TaskTemplateKey.SEND_CONTRACT],
		cta: {
			label: "Générer le contrat",
			actionType: TaskCTAType.MODAL,
			modalKey: "generate-contract",
		},
	},
	[TaskTemplateKey.SEND_CONTRACT]: {
		defaultTitle: "Envoyer le contrat en signature",
		defaultAssignmentRole: RoleEnum.BROKER,
		visibilityRoles: [RoleEnum.DUNE],
		dashboardVisibilityRoles: [RoleEnum.BROKER],
		next: [TaskTemplateKey.SIGN_CONTRACT_BROKER],
		cta: {
			label: "Envoyer pour signature",
			actionType: TaskCTAType.MODAL,
			modalKey: "send-contract",
		},
	},
	[TaskTemplateKey.SIGN_CONTRACT_BROKER]: {
		defaultTitle: "Contrat à signer Apporteur",
		visibilityRoles: [RoleEnum.DUNE, RoleEnum.BROKER],
		dueInDays: 30,
		next: [TaskTemplateKey.SIGN_CONTRACT_DUNE],
	},
	[TaskTemplateKey.SIGN_CONTRACT_DUNE]: {
		defaultTitle: "Contrat à signer DUNE",
		defaultAssignmentRole: RoleEnum.DUNE,
		visibilityRoles: [RoleEnum.DUNE],
		dashboardVisibilitySubRoles: [SubRoleEnum.DUNE_UNDERWRITER],
		dueInDays: 1,
		cta: {
			label: "Signature Contrat par DUNE",
			actionType: TaskCTAType.MODAL,
			modalKey: "sign-contract-dune",
		},
	},
	[TaskTemplateKey.GENERATE_FINAL_CERTIFICATE]: {
		defaultTitle: "Générer l'attestation définitive",
		defaultAssignmentRole: RoleEnum.DUNE,
		visibilityRoles: [RoleEnum.DUNE],
		dashboardVisibilitySubRoles: [SubRoleEnum.DUNE_UNDERWRITER],
		dueInDays: 1,
		next: [TaskTemplateKey.SEND_END_OF_WORK_AMENDMENT],
		cta: {
			label: "Générer l'attestation définitive",
			actionType: TaskCTAType.MODAL,
			modalKey: "generate-final-certificate",
		},
	},
	[TaskTemplateKey.GENERATE_QUITTANCEMENT_RECEPTION]: {
		defaultTitle: "Quittancement réception",
		defaultAssignmentRole: RoleEnum.DUNE,
		visibilityRoles: [RoleEnum.DUNE],
		dashboardVisibilitySubRoles: [SubRoleEnum.DUNE_UNDERWRITER],
		dueInDays: 1,
		cta: {
			label: "Générer la quittance de réception",
			actionType: TaskCTAType.MODAL,
			modalKey: "generate-receipt-reception",
		},
	},
	[TaskTemplateKey.SEND_END_OF_WORK_AMENDMENT]: {
		defaultTitle: "Envoyer l'Avenant Fin de Travaux en signature",
		defaultAssignmentRole: RoleEnum.BROKER,
		visibilityRoles: [RoleEnum.DUNE],
		dashboardVisibilityRoles: [RoleEnum.BROKER],
		next: [TaskTemplateKey.SIGN_END_OF_WORK_AMENDMENT_BROKER],
		cta: {
			label: "Envoyer pour signature",
			actionType: TaskCTAType.MODAL,
			modalKey: "send-end-of-work-amendment",
		},
	},
	[TaskTemplateKey.SIGN_END_OF_WORK_AMENDMENT_BROKER]: {
		defaultTitle: "Avenant Fin de Travaux à signer Apporteur",
		visibilityRoles: [RoleEnum.DUNE, RoleEnum.BROKER],
		dueInDays: 30,
		next: [TaskTemplateKey.SIGN_END_OF_WORK_AMENDMENT_DUNE],
	},
	[TaskTemplateKey.SIGN_END_OF_WORK_AMENDMENT_DUNE]: {
		defaultTitle: "Avenant Fin de Travaux à signer DUNE",
		defaultAssignmentRole: RoleEnum.DUNE,
		visibilityRoles: [RoleEnum.DUNE],
		dashboardVisibilitySubRoles: [SubRoleEnum.DUNE_UNDERWRITER],
		dueInDays: 1,
		next: [TaskTemplateKey.GENERATE_QUITTANCEMENT],
		cta: {
			label: "Signer l'Avenant Fin de Travaux",
			actionType: TaskCTAType.MODAL,
			modalKey: "sign-end-of-work-amendment-dune",
		},
	},
	[TaskTemplateKey.GENERATE_PROPOSITION_DOCUMENT]: {
		defaultTitle: "Générer Documents",
		defaultAssignmentRole: RoleEnum.DUNE,
		visibilityRoles: [RoleEnum.DUNE],
		dueInDays: 1,
		cta: {
			label: "Générer les documents",
			actionType: TaskCTAType.MODAL,
			modalKey: "generate-proposition-document",
		},
	},

	// GFA tasks
	[TaskTemplateKey.SUBMIT_GFA]: {
		defaultTitle: "Soumission GFA",
		defaultAssignmentRole: RoleEnum.BROKER,
		visibilityRoles: [RoleEnum.BROKER, RoleEnum.DUNE],
		next: [TaskTemplateKey.ANALYZE_GFA_SUBMISSION],
		cta: {
			label: "Soumettre le chantier GFA",
			actionType: TaskCTAType.REDIRECT,
			navigationPath: APP_PAGES.GFA.edit.index,
		},
	},
	[TaskTemplateKey.ANALYZE_GFA_SUBMISSION]: {
		defaultTitle: "Analyse Projet GFA",
		defaultAssignmentRole: RoleEnum.DUNE,
		visibilityRoles: [RoleEnum.DUNE],
		dashboardVisibilitySubRoles: [SubRoleEnum.DUNE_UNDERWRITER],
		dueInDays: 3,
		next: [TaskTemplateKey.GENERATE_GFA_DOCUMENT],
		cta: {
			label: "Analyser le projet GFA",
			actionType: TaskCTAType.MODAL,
			modalKey: "analyze-gfa-submission",
		},
	},
	[TaskTemplateKey.GENERATE_GFA_DOCUMENT]: {
		defaultTitle: "Générer Documents",
		defaultAssignmentRole: RoleEnum.DUNE,
		visibilityRoles: [RoleEnum.DUNE],
		dueInDays: 1,
		cta: {
			label: "Générer les documents GFA",
			actionType: TaskCTAType.MODAL,
			modalKey: "generate-gfa-document",
		},
	},

	// Reminder tasks
	[TaskTemplateKey.REMINDER_SIGN_PROPOSITION_1]: {
		defaultTitle: "Relance signature Proposition 1",
		defaultAssignmentRole: RoleEnum.BROKER,
		visibilityRoles: [RoleEnum.DUNE, RoleEnum.BROKER],
		next: [TaskTemplateKey.SIGN_PROPOSITION_DUNE],
		cta: {
			label: "Uploader ou envoyer pour signature",
			actionType: TaskCTAType.MODAL,
			modalKey: "upload-signed-proposition",
		},
	},
	[TaskTemplateKey.REMINDER_SIGN_PROPOSITION_2]: {
		defaultTitle: "Relance signature Proposition 2",
		defaultAssignmentRole: RoleEnum.BROKER,
		visibilityRoles: [RoleEnum.DUNE, RoleEnum.BROKER],
		next: [TaskTemplateKey.SIGN_PROPOSITION_DUNE],
		cta: {
			label: "Uploader ou envoyer pour signature",
			actionType: TaskCTAType.MODAL,
			modalKey: "upload-signed-proposition",
		},
	},
	[TaskTemplateKey.REMINDER_SIGN_CONTRACT_1]: {
		defaultTitle: "Relance signature Contrat 1",
		defaultAssignmentRole: RoleEnum.BROKER,
		visibilityRoles: [RoleEnum.DUNE, RoleEnum.BROKER],
		next: [TaskTemplateKey.SIGN_CONTRACT_DUNE],
		cta: {
			label: "Upload contrat signé ou envoi pour signature DocuSign",
			actionType: TaskCTAType.MODAL,
			modalKey: "upload-signed-contract",
		},
	},
	[TaskTemplateKey.REMINDER_SIGN_CONTRACT_2]: {
		defaultTitle: "Relance signature Contrat 2",
		defaultAssignmentRole: RoleEnum.BROKER,
		visibilityRoles: [RoleEnum.DUNE, RoleEnum.BROKER],
		next: [TaskTemplateKey.SIGN_CONTRACT_DUNE],
		cta: {
			label: "Upload contrat signé ou envoi pour signature DocuSign",
			actionType: TaskCTAType.MODAL,
			modalKey: "upload-signed-contract",
		},
	},
	[TaskTemplateKey.REMINDER_SIGN_END_OF_WORK_AMENDMENT_1]: {
		defaultTitle: "Relance signature Avenant Fin de travaux 1",
		defaultAssignmentRole: RoleEnum.BROKER,
		visibilityRoles: [RoleEnum.DUNE, RoleEnum.BROKER],
		cta: {
			label: "Upload avenant fin de travaux signé ou envoi pour signature DocuSign",
			actionType: TaskCTAType.MODAL,
			modalKey: "upload-signed-end-amendment",
		},
	},
	[TaskTemplateKey.REMINDER_SIGN_END_OF_WORK_AMENDMENT_2]: {
		defaultTitle: "Relance signature Avenant Fin de travaux 2",
		defaultAssignmentRole: RoleEnum.BROKER,
		visibilityRoles: [RoleEnum.DUNE, RoleEnum.BROKER],
		cta: {
			label: "Upload avenant fin de travaux signé ou envoi pour signature DocuSign",
			actionType: TaskCTAType.MODAL,
			modalKey: "upload-signed-end-amendment",
		},
	},
	[TaskTemplateKey.REMINDER_PAYMENT_PRIME_1]: {
		defaultTitle: "Relance paiement Prime 1",
		defaultAssignmentRole: RoleEnum.BROKER,
		visibilityRoles: [RoleEnum.DUNE, RoleEnum.BROKER],
		next: [TaskTemplateKey.GENERATE_CONTRACT],
		cta: {
			label: "Paiement de la prime",
			actionType: TaskCTAType.MODAL,
			modalKey: "payment-prime",
		},
	},
	[TaskTemplateKey.REMINDER_PAYMENT_PRIME_2]: {
		defaultTitle: "Relance paiement Prime 2",
		defaultAssignmentRole: RoleEnum.BROKER,
		visibilityRoles: [RoleEnum.DUNE, RoleEnum.BROKER],
		next: [TaskTemplateKey.GENERATE_CONTRACT],
		cta: {
			label: "Paiement de la prime",
			actionType: TaskCTAType.MODAL,
			modalKey: "payment-prime",
		},
	},
	[TaskTemplateKey.REMINDER_PAYMENT_PRIME_RECEPTION_1]: {
		defaultTitle: "Relance paiement Prime Réception 1",
		defaultAssignmentRole: RoleEnum.BROKER,
		visibilityRoles: [RoleEnum.DUNE, RoleEnum.BROKER],
		next: [TaskTemplateKey.GENERATE_FINAL_CERTIFICATE],
		cta: {
			label: "Paiement de la prime réception",
			actionType: TaskCTAType.MODAL,
			modalKey: "payment-prime-reception",
		},
	},
	[TaskTemplateKey.REMINDER_PAYMENT_PRIME_RECEPTION_2]: {
		defaultTitle: "Relance paiement Prime Réception 2",
		defaultAssignmentRole: RoleEnum.BROKER,
		visibilityRoles: [RoleEnum.DUNE, RoleEnum.BROKER],
		next: [TaskTemplateKey.GENERATE_FINAL_CERTIFICATE],
		cta: {
			label: "Paiement de la prime réception",
			actionType: TaskCTAType.MODAL,
			modalKey: "payment-prime-reception",
		},
	},

	[TaskTemplateKey.POLICE_TERMINATION]: {
		defaultTitle: "Résiliation police",
		defaultAssignmentRole: RoleEnum.DUNE,
		visibilityRoles: [RoleEnum.DUNE],
	},
};

export const SIGNATURE_TASK_CONFIG = {
	PROPOSITION: {
		send: TaskTemplateKey.SEND_PROPOSITION,
		signBroker: TaskTemplateKey.SIGN_PROPOSITION_BROKER,
		signDune: TaskTemplateKey.SIGN_PROPOSITION_DUNE,
	},
	CONTRACT: {
		send: TaskTemplateKey.SEND_CONTRACT,
		signBroker: TaskTemplateKey.SIGN_CONTRACT_BROKER,
		signDune: TaskTemplateKey.SIGN_CONTRACT_DUNE,
	},
	END_OF_WORK_AMENDMENT: {
		send: TaskTemplateKey.SEND_END_OF_WORK_AMENDMENT,
		signBroker: TaskTemplateKey.SIGN_END_OF_WORK_AMENDMENT_BROKER,
		signDune: TaskTemplateKey.SIGN_END_OF_WORK_AMENDMENT_DUNE,
	},
} as const;